import React from "react";
import { InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import * as CuboidTask from "../../../dzial8_stereometria/temat4_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/MaturaCuboidGeoSequenceVolumeOpenTask";

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.raw != null) return rawValue(value.raw);
  return String(value).trim();
}

function preview(prefix, value) {
  const raw = rawValue(value);
  if (!raw) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <strong>Podgląd:</strong>{" "}
      <InlineMath math={`${prefix}=${raw}`} />
    </div>
  );
}

export function generateTask(options = {}) {
  const base = CuboidTask.generateTask({
    ...options,
    variant: "random",
    session: undefined,
  });

  return {
    ...base,
    type: "MaturaGeometricEdgeRatioOpenTask",
    data: {
      ...base.data,
      coursePart: "edge-ratio",
      maxPoints: 1,
    },
    source: {
      variant: "course",
      basedOn: "CKE_P_2025_CZE_24",
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne - otwarte (0-1 pkt).</strong></p>
      <p>
        Długości trzech krawędzi prostopadłościanu wychodzących z jednego
        wierzchołka tworzą ciąg geometryczny o ilorazie{" "}
        <InlineMath math={`q=${data.ratio}`} />.
      </p>
      <p>
        Zapisz proporcje długości tych krawędzi. Możesz podać współczynniki
        albo jednomiany z tą samą niewiadomą.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  const setField = (key) => (value) => onChange({ ...answer, [key]: value });

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 460 }}>
      {["k1", "k2", "k3"].map((key, index) => (
        <div key={key}>
          <div style={{ marginBottom: 5 }}>
            <InlineMath math={`k_${index + 1}=`} />
          </div>
          <EquationInput
            value={answer[key]}
            onChange={setField(key)}
            disabled={disabled}
            placeholder={index === 0 ? "np. x" : index === 1 ? "np. 5x" : "np. 25x"}
            ariaLabel={`${index + 1}. długość krawędzi`}
            showPreview={false}
          />
          {preview(`k_${index + 1}`, answer[key])}
        </div>
      ))}
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const baseResult = CuboidTask.checkAnswer(task, userAnswer);
  const ratioOk = Boolean(baseResult?.details?.ratioOk);

  return {
    correct: ratioOk,
    isCorrect: ratioOk,
    score: ratioOk ? 1 : 0,
    maxScore: 1,
    parts: { ratios: ratioOk },
    details: { ratioOk },
  };
}

export function renderAnswer({ data }) {
  return (
    <div>
      <InlineMath math={`1,\\;${data.ratio},\\;${data.ratio ** 2}`} />
      {" lub "}
      <InlineMath math={`x,\\;${data.ratio}x,\\;${data.ratio ** 2}x`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const values = ["k1", "k2", "k3"].map((key) => rawValue(userAnswer?.[key]));
  if (values.every((value) => !value)) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={values.map((value) => value || "-").join(",\\;")} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <p>
        <strong>Krok 1.</strong> Najkrótszą krawędź oznaczamy przez{" "}
        <InlineMath math="x" />.
      </p>
      <p>
        <strong>Krok 2.</strong> Każdy kolejny wyraz ciągu geometrycznego
        otrzymujemy, mnożąc poprzedni przez{" "}
        <InlineMath math={`q=${data.ratio}`} />.
      </p>
      <p>
        Zatem proporcje krawędzi to{" "}
        <InlineMath math={`x:\\!${data.ratio}x:\\!${data.ratio ** 2}x`} />,
        czyli również <InlineMath math={`1:\\!${data.ratio}:\\!${data.ratio ** 2}`} />.
      </p>
    </div>
  );
}
